import { JsonSchema, UISchemaElement } from "@jsonforms/core"

/**
 * Enum for the form render mode.
 */
export enum FormRenderMode {
  EDIT = "edit",
  SUMMARY = "summary",
}

/**
 * Enum for the form type.
 */
export enum FormType {
  SMARTRX = "SMARTRX",
  UASSIST = "UASSIST",
}

/**
 * Props for a form mutator component (for editing schema, data, etc.).
 */
export type FormMutatorProps = {
  //--------
  fetching: FormFetchingStatus
  showForm: boolean
  setShowForm: (value: any) => void
  //--------
  name: string
  // --
  initialSchema: any
  schema: any
  setSchema: (value: any) => void
  // --
  initialUiSchema: any
  uiSchema: any
  setUiSchema: (value: any) => void
  // --
  initialData: any
  data: any
  setData: (value: any) => void
}

/**
 * Enum for the form fetching status.
 */
export enum FormFetchingStatus{
  INITIAL = 'initial',
  FETCHING = "fetching",
  COMPLETED = 'completed',
  REJECTED = 'rejected'
}

/**
 * Type for the rendered template used in the form context.
 */
export type RenderedTemplateType = null | {
  showTemplate: boolean
  template: string
  schema: any
  uiSchema: any
  data: any
  defaultData: any
  errors: any
  isTransitioning?: boolean
}

/**
 * The Type of the FormContext value.
 */
export interface FormContextType {
  details: FormContentDetails | undefined
  formLoading: boolean
  setFormLoading: (loading: boolean) => void
  formType: FormType | null
  setFormType: (type: FormType | null) => void
  formName: string | null
  setFormName: (name: string | null) => void
  schema: JsonSchema
  setSchema: (schema: JsonSchema) => void
  uiSchema: UISchemaElement
  setUiSchema: (uiSchema: UISchemaElement) => void
  data: Record<string, any>
  setData: (data: Record<string, any>) => void
  saveId: string | null
  setSaveId: (saveId: string | null) => void
  formErrors: Record<string, string>
  setFormErrors: (errors: Record<string, string>) => void
  showValidationErrors: boolean
  setShowValidationErrors: (showValidationErrors: boolean) => void
  mode: FormRenderMode
  setMode: (mode: FormRenderMode) => void
  fetchTemplate: (template: any) => Promise<void>
  renderedTemplate: RenderedTemplateType
  setRenderedTemplate: (renderedTemplate: RenderedTemplateType) => void
  routeToEditTemplateForm: (templateVal: {
    value: string
    id: string
  }) => Promise<void>
  routeBackToForm: (formSaved?: boolean) => void
  nestedFormRefs: Map<string, NestedFormRef>
  registerNestedFormRef: (path: string, ref: NestedFormRef) => void
  unregisterNestedFormRef: (path: string) => void
  uiSchemaVersion: string | null
  setUiSchemaVersion: (version: string | null) => void
}

/**
 * Props for the FormProvider component.
 */
export interface FormProviderProps {
  children: React.ReactNode
  details: FormContentDetails | undefined
  showValidationErrors?: boolean
  mode?: FormRenderMode
}

/**
 * Ref for imperative methods exposed by the FormContent component.
 */
export interface FormContentRef {
  saveFormData: () => Promise<void>
  validateErrors: () => Promise<boolean>
  setShowValidationErrors: (showValidationErrors: boolean) => void
  showValidationErrors: boolean
  submitToUassist: () => Promise<void>
  data: any
  saveSmartRxForm: () => void
  formName?: string
  resetFormData: () => Promise<void>
  exportComponentAsPDF?: (status: string) => void
}

/**
 * Props for the FormContent component.
 */
export interface displayMetaType {
  formTitle?: string | null
  formDescription?: string | null
}

export interface FormContentDetails {
  patientId?: string
  caseId?: string
  formType: FormType
  formId?: number | string
  formName?: string
  uassistRound: number
  fetchSavedData: boolean
  saveId?: string
  isSettings?: boolean
  hideTitle?: boolean
  displayMeta?: displayMetaType | ((formName: string) => displayMetaType)
  isNestedForm?: boolean
}

export interface FormContentProps {
  details?: FormContentDetails
  onChange?: (data: Record<string, any>) => void
  onFormSaved?: (val: boolean) => void
  setCurrentFormType?: (formType: FormType | null) => void
  unlockFunc?: () => void
  onSavingChange?: (saving: boolean) => void
  exportComponentAsPDF?: (status: string) => void
}

/**
 * Props for the Form component.
 */
export interface FormProps {
  details?: FormContentProps["details"]
  mode?: FormRenderMode
  onChange?: FormContentProps["onChange"]
  showValidationErrors?: boolean
  onFormSaved?: FormContentProps["onFormSaved"]
  setCurrentFormType?: FormContentProps["setCurrentFormType"]
  unlockFunc?: () => void
  onSavingChange?: FormContentProps["onSavingChange"]
  exportComponentAsPDF?: FormContentProps["exportComponentAsPDF"]

}

/**
 * Interface for nested form reference tracking
 */
export interface NestedFormRef {
  path: string
  validate: () => Promise<boolean>
}